// The reflections as one flat picture, on every device (VIS-267; the owner's pick 2026-11-03: "the faster reflections
// look no different to me so lets go with that"). On iOS a texture type costs a program its full compile once per stage
// (cube 302 ms, about 12 s of a first visit in all); every lit fragment already has a texture_2d<f32>, so a flat picture
// adds nothing there. His phone, forced first visit: 38 s with the cube, 30 s with this.
//
// three's PMREMGenerator stays as it is and bakes its cube: same filter, same levels (256 ... 8 a face). The cube's
// levels are drawn here into one picture, and scene.environmentNode (three's public door) reads that picture. Both
// readers go through it: lazy/daylight.js and three's own EnvironmentNode. Only this picture's writer reads a cube.
//
// Each face takes all its cube texels, texel for texel, plus one texel round it read across the seam from the cube, so
// a lookup here gives what the cube's own filter gives, seams too (r186's atlas squeezed the face into size - 2 texels).
//
//   774 x 776, rgba16float, no mips (4.8 MB; the cube it replaces at rest 4.2 MB)
//   row y 0:   level 0, faces 3 x 2 of 258 x 258
//   row y 516: levels 1 ... 5 side by side, each 3 x 2 of (size + 2)
//
// A level's six faces are one draw, so a reflection fade costs one draw and one pass a level, not six of each; levels
// that share a blend are one draw too (VIS-267: computers' fade, a jump): each vertex carries its level (position z),
// the tiles never overlap, so each texel gets what its own level's draw gave it.
import * as THREE from 'three/webgpu';
import { Fn, If, float, vec2, vec3, vec4, abs, floor, fract, exp2, min, max, mix, step, uniform, attribute, positionGeometry, texture, cubeTexture } from 'three/tsl';

const SIZE = 256, LODS = 6, LAST = LODS - 1, W = 3 * (SIZE + 2), H = 2 * (SIZE + 2) + 2 * (SIZE / 2 + 2);

/** The level's left top corner in the picture (the same sums as atlasUV below). */
const originX = (lod) => Math.max(3 * (SIZE - 2 * (SIZE >> lod)) + 6 * (lod - 1), 0), originY = (lod) => Math.min(lod, 1) * 2 * (SIZE + 2);

/** Six quads a level, the level in position z. dir = the world direction at that point of the face, past the face's edge on its one texel border. */
function atlasGeometry() {
  const pos = new Float32Array(LODS * 6 * 6 * 3), dir = new Float32Array(LODS * 6 * 6 * 3);
  const FACE = [(u, v) => [1, v, u], (u, v) => [u, 1, v], (u, v) => [u, v, 1], (u, v) => [-1, v, u], (u, v) => [u, -1, v], (u, v) => [u, v, -1]];
  let p = 0, d = 0;
  for (let lod = 0; lod < LODS; lod++) {
    const S = SIZE >> lod, T = S + 2, e = T / S; // the tile runs to +-e on the face's plane: two texels of 2 / S past +-1, half of each outside
    for (let f = 0; f < 6; f++) {
      const x0 = originX(lod) + (f % 3) * T, y0 = originY(lod) + (f >= 3 ? T : 0);
      for (const [cx, cy] of [[0, 0], [1, 0], [1, 1], [0, 0], [1, 1], [0, 1]]) {
        pos[p++] = (2 * (x0 + cx * T)) / W - 1; pos[p++] = 1 - (2 * (y0 + cy * T)) / H; pos[p++] = lod;
        const a = FACE[f]((2 * cx - 1) * e, (2 * cy - 1) * e);
        dir[d++] = a[0]; dir[d++] = a[1]; dir[d++] = a[2];
      }
    }
  }
  const g = new THREE.BufferGeometry();
  g.setAttribute('position', new THREE.BufferAttribute(pos, 3));
  g.setAttribute('dir', new THREE.BufferAttribute(dir, 3));
  return g;
}

/** World direction -> (u, v) on its face (-1 .. 1) and the face (0 +x, 1 +y, 2 +z, 3 -x, 4 -y, 5 -z). */
const envFace = /*@__PURE__*/ Fn(([d]) => {
  const a = abs(d).toVar(), r = vec3(0).toVar();
  If(a.x.greaterThanEqual(a.y).and(a.x.greaterThanEqual(a.z)), () => { r.assign(vec3(d.z.div(a.x), d.y.div(a.x), step(0, d.x.negate()).mul(3))); })
    .ElseIf(a.y.greaterThanEqual(a.z), () => { r.assign(vec3(d.x.div(a.y), d.z.div(a.y), step(0, d.y.negate()).mul(3).add(1))); })
    .Else(() => { r.assign(vec3(d.x.div(a.z), d.y.div(a.z), step(0, d.z.negate()).mul(3).add(2))); });
  return r;
}).setLayout({ name: 'envFace', type: 'vec3', inputs: [{ name: 'd', type: 'vec3' }] });

/** That point of a level in the picture, 0 .. 1 (originX, originY in the shader's numbers). */
const atlasUV = /*@__PURE__*/ Fn(([p, lod]) => {
  const S = float(SIZE).div(exp2(lod)).toVar(), row = step(3, p.z).toVar();
  const origin = vec2(max(float(SIZE).sub(S.mul(2)).mul(3).add(lod.sub(1).mul(6)), 0), min(lod, 1).mul(2 * (SIZE + 2)));
  return vec2(p.z.sub(row.mul(3)), row).mul(S.add(2)).add(p.xy.mul(0.5).add(0.5).mul(S)).add(1).add(origin).div(vec2(W, H));
}).setLayout({ name: 'envAtlasUV', type: 'vec2', inputs: [{ name: 'p', type: 'vec3' }, { name: 'lod', type: 'float' }] });

/** What three's PMREMNode does, on the picture: reads the direction and the roughness from the context around it. */
class EnvAtlasNode extends THREE.Node {
  static get type() { return 'EnvAtlasNode'; }
  constructor(map) { super('vec3'); this.map = texture(map); }
  setup(builder) {
    const p = envFace(builder.context.getUV(this, builder)).toVar(), level = builder.context.getTextureLevel(this);
    const read = (lod) => this.map.sample(atlasUV(p, lod)).level(0).rgb;
    if (level.isConstNode && level.value === 1) return read(float(LAST)); // three's diffuse lookup: only the roughest level
    const r = float(level).clamp(), m = float(LAST).mul(r).mul(float(2).sub(r)).toVar(), lo = floor(m).toVar(); // PMREMGenerator's roughness -> level (r187 roughnessToMip)
    return mix(read(lo), read(min(lo.add(1), LAST)), fract(m));
  }
}

/** The picture, its reader for scene.environmentNode, and the draw of a cube's level into it. */
export function createEnvAtlas(renderer) {
  const target = new THREE.RenderTarget(W, H, { type: THREE.HalfFloatType, format: THREE.RGBAFormat, colorSpace: THREE.LinearSRGBColorSpace, minFilter: THREE.LinearFilter, magFilter: THREE.LinearFilter, generateMipmaps: false, depthBuffer: false });
  target.texture.name = 'PMREM.atlas';

  const blank = new THREE.CubeTexture();
  blank.isRenderTargetTexture = true; // as PMREMNode's own, so the read is the same on the WebGL2 fallback
  const cube = cubeTexture(blank), share = uniform(1);
  const mat = new THREE.NodeMaterial();
  mat.name = 'PMREM_atlas';
  mat.vertexNode = vec4(positionGeometry.xy, 0, 1);
  mat.fragmentNode = vec4(cube.sample(attribute('dir', 'vec3')).level(positionGeometry.z.round()).rgb, share); // src x share + dst x (1 - share); the level: equal at a quad's corners, rounded to the whole number the uniform was
  Object.assign(mat, { side: THREE.DoubleSide, transparent: true, depthTest: false, depthWrite: false, toneMapped: false, fog: false });
  const mesh = new THREE.Mesh(atlasGeometry(), mat), cam = new THREE.OrthographicCamera();
  mesh.frustumCulled = false;

  /** n levels of `cubeTex` from `lod` into the picture, in one draw; k = the share of what is left of a fade, 1 = replace. */
  function draw(cubeTex, lod, k = 1, n = 1) {
    const rt = renderer.getRenderTarget(), face = renderer.getActiveCubeFace(), mip = renderer.getActiveMipmapLevel(), mrt = renderer.getMRT(), ac = renderer.autoClear;
    cube.value = cubeTex; share.value = Math.max(k, 0); // (a clock stepped back gives a negative share: nothing moves, this picture holds its value)
    mesh.geometry.setDrawRange(lod * 36, n * 36);
    renderer.autoClear = false;
    renderer.setMRT(null);
    renderer.setRenderTarget(target);
    try { renderer.render(mesh, cam); } finally { // (a draw that throws must not leave the frame drawing into this picture)
      renderer.setRenderTarget(rt, face, mip);
      renderer.setMRT(mrt);
      renderer.autoClear = ac;
    }
  }
  return { target, node: new EnvAtlasNode(target.texture), draw, drawAll: (cubeTex) => draw(cubeTex, 0, 1, LODS) };
}
